import {compact} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useMemo, useState} from 'react';

import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {globalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {getUser} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {CompanyDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/companyDropdownItem';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {
  SearchDropdown,
  SearchDropdownProps,
} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {TypedRowValue} from '../../../../../libs/shared/ui/src/components/dropdowns/source/dropdownSource';
import {TeamDropdownItemContainer} from '../../../../../libs/shared/ui/src/components/dropdowns/teamDropdownItemContainer';
import {makeFilterRows} from '../../../../analytics/helpers/analyticsFiltersHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {UserDropdownItem} from '../userDropdownItem';

/*
 * Constants.
 */

enum SearchNamespacesDropdownItemsEnum {
  USER = 'user',
  TEAM = 'team',
  COMPANY = 'company',
}

/*
 * Props.
 */

interface SearchNamespacesDropdownRenderProps {
  /** Should the user be included in the dropdown list? */
  shouldIncludeUser?: boolean;
  /** Should the company be included in the dropdown list? */
  shouldIncludeCompany?: boolean;
  /** Should user namespace appear in first or last position? */
  shouldPrioritizeUser?: boolean;
  /** Should the dropdown display the visibility description */
  shouldRenderVisibilityDescription?: boolean;
  /** Control how the user dropdown item name is displayed */
  renderUserDropdownItemName?: (user: TeammateModel) => React.ReactNode;
}

export interface SearchNamespacesDropdownProps
  extends DropdownProps,
    Pick<
      SearchDropdownProps<SearchNamespacesDropdownRowTypedValue>,
      'headerTitle' | 'headerRightIcon' | 'onHeaderBackButtonClick' | 'headerBackButtonLabel'
    >,
    SearchNamespacesDropdownRenderProps {
  selectedNamespace: string | undefined;
  teams: ReadonlyArray<TeamModel>;
  onChange: (namespace: string) => void;
}

type SearchNamespacesDropdownRowTypedValue =
  | TypedRowValue<SearchNamespacesDropdownItemsEnum.USER, TeammateModel>
  | TypedRowValue<SearchNamespacesDropdownItemsEnum.TEAM, TeamModel>
  | TypedRowValue<SearchNamespacesDropdownItemsEnum.COMPANY, string>;

/*
 * Components.
 */

export const SearchNamespacesDropdown: FC<SearchNamespacesDropdownProps> = (props) => {
  const user = useAppSelector(getUser);

  const [query, setQuery] = useState('');
  const data = useMemo(() => buildDropdownItems(props, user), [props, user]);
  const filteredData = useMemo(() => filterRows(data, query), [data, query]);

  const renderRow = (item: SearchNamespacesDropdownRowTypedValue) => {
    switch (item.type) {
      case SearchNamespacesDropdownItemsEnum.USER:
        return renderUserItem(props, item.value);
      case SearchNamespacesDropdownItemsEnum.TEAM:
        return renderTeamItem(props, item.value);
      case SearchNamespacesDropdownItemsEnum.COMPANY:
        return renderCompanyItem(props, item.value);
      default:
        throw new UnreachableError(item);
    }
  };

  return (
    <SearchDropdown
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      data={filteredData}
      query={query}
      onQueryChange={setQuery}
      renderRow={renderRow}
    />
  );
};

/*
 * Helpers.
 */

function buildDropdownItems(
  {shouldIncludeUser, shouldIncludeCompany, shouldPrioritizeUser, teams}: SearchNamespacesDropdownProps,
  user: TeammateModel,
): ReadonlyArray<SearchNamespacesDropdownRowTypedValue> {
  const items = compact([
    shouldIncludeCompany ? buildCompanyDropdownItem() : undefined,
    ...teams.map(buildTeamDropdownItem),
    shouldIncludeUser ? buildUserDropdownItem(user) : undefined,
  ]);

  return shouldPrioritizeUser ? items.reverse() : items;
}

function buildUserDropdownItem(value: TeammateModel): SearchNamespacesDropdownRowTypedValue {
  return {
    type: SearchNamespacesDropdownItemsEnum.USER,
    value,
  };
}

function buildTeamDropdownItem(value: TeamModel): SearchNamespacesDropdownRowTypedValue {
  return {
    type: SearchNamespacesDropdownItemsEnum.TEAM,
    value,
  };
}

function buildCompanyDropdownItem(): SearchNamespacesDropdownRowTypedValue {
  return {
    type: SearchNamespacesDropdownItemsEnum.COMPANY,
    value: globalNamespace,
  };
}

function renderUserItem(props: SearchNamespacesDropdownProps, user: TeammateModel) {
  const {selectedNamespace, shouldRenderVisibilityDescription, renderUserDropdownItemName, onChange} = props;

  const isChecked = selectedNamespace === user.namespace;
  return (
    <UserDropdownItem
      key={user.id}
      isChecked={isChecked}
      onSelect={() => onChange(user.namespace)}
      renderUserItemName={renderUserDropdownItemName ? () => renderUserDropdownItemName(user) : undefined}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}

function renderTeamItem(props: SearchNamespacesDropdownProps, team: TeamModel) {
  const {selectedNamespace, shouldRenderVisibilityDescription, onChange} = props;
  return (
    <TeamDropdownItemContainer
      key={team.id}
      team={team}
      isChecked={selectedNamespace === team.namespace}
      onSelect={() => onChange(team.namespace)}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}

function renderCompanyItem(props: SearchNamespacesDropdownProps, namespace: string) {
  const {selectedNamespace, shouldRenderVisibilityDescription, onChange} = props;

  const isChecked = selectedNamespace === namespace;
  return (
    <CompanyDropdownItem
      isChecked={isChecked}
      onSelect={() => onChange(globalNamespace)}
      shouldRenderVisibilityDescription={shouldRenderVisibilityDescription}
    />
  );
}

const filterRows = memoize(20)(
  makeFilterRows<SearchNamespacesDropdownRowTypedValue>((item) => {
    switch (item.type) {
      case SearchNamespacesDropdownItemsEnum.USER:
        return item.value.alias;
      case SearchNamespacesDropdownItemsEnum.TEAM:
        return item.value.name;
      case SearchNamespacesDropdownItemsEnum.COMPANY:
        return item.value;
      default:
        throw new UnreachableError(item);
    }
  }),
);
